import React, { useEffect, useState } from 'react';
import { fetchMinisiteLeaderboard } from '../../api/minisiteLeaderboard';
import './MinisiteShared.css';

function formatMonthHebrew(monthKey) {
  const [y, m] = monthKey.split('-').map((x) => parseInt(x, 10));
  if (!y || !m) return monthKey;
  const d = new Date(y, m - 1, 1);
  return new Intl.DateTimeFormat('he-IL', { month: 'long', year: 'numeric' }).format(d);
}

/**
 * @param {object} props
 * @param {'snake'|'memory'|'invaders'|'slots'|'poker'|'blackjack'} props.game
 * @param {string} props.scoreLabel — e.g. "ניקוד" or "מהלכים"
 * @param {() => void} props.onContinue — any key
 * @param {(entries: Array, monthKey: string) => void} [props.onEntriesLoaded]
 */
function MonthlyLeadersScreen({ game, scoreLabel, onContinue, onEntriesLoaded }) {
  const [monthKey, setMonthKey] = useState('');
  const [entries, setEntries] = useState([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchMinisiteLeaderboard(game);
        if (cancelled) return;
        setMonthKey(data.monthKey || '');
        const ent = Array.isArray(data.entries) ? data.entries : [];
        setEntries(ent);
        setUnavailable(!!data.unavailable);
        if (onEntriesLoaded) onEntriesLoaded(ent, data.monthKey || '');
      } catch (e) {
        if (!cancelled) setError(e.message || 'שגיאה');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [game, onEntriesLoaded]);

  useEffect(() => {
    if (loading) return undefined;
    const onKey = (e) => {
      e.preventDefault();
      onContinue();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [loading, onContinue]);

  return (
    <div className="minisite-leaders" dir="rtl">
      <header className="minisite-leaders-header">
        <h1 className="minisite-leaders-title">מצטייני החודש</h1>
        {monthKey ? (
          <p className="minisite-leaders-sub">{formatMonthHebrew(monthKey)}</p>
        ) : null}
      </header>

      <section className="minisite-leaders-body" aria-live="polite">
        {loading && <p className="minisite-leaders-msg">טוען…</p>}
        {error && <p className="minisite-leaders-err">{error}</p>}
        {unavailable && !loading && !error && (
          <p className="minisite-leaders-msg">הטבלה זמנית לא זמינה — המשחק זמין כרגיל.</p>
        )}

        {!loading && !error && (
          <div className="minisite-leaders-main">
            <ol className="minisite-leaders-list" start={1}>
              {entries.length === 0 && !unavailable ? (
                <li className="minisite-leaders-empty">אין עדיין רשומות לחודש זה — תהיה הראשון!</li>
              ) : (
                entries.map((row, i) => (
                  <li
                    key={`${row.name}-${row.score}-${i}`}
                    className={`minisite-leaders-row${i < 3 ? ' minisite-leaders-row--top' : ''}`}
                  >
                    <span className="minisite-leaders-rank">{i + 1}.</span>
                    <span className="minisite-leaders-name">{row.name}</span>
                    <span className="minisite-leaders-score">
                      {scoreLabel}: {row.score}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>
        )}
      </section>

      <footer className="minisite-leaders-footer">
        <p className="minisite-leaders-hint">כל מקש בשלט להמשך למשחק</p>
      </footer>
    </div>
  );
}

export default MonthlyLeadersScreen;
